import i18n from "./i18n/i18n";

interface HTMLValidationElement extends HTMLInputElement {
  _validator?: Validator | null,
}

type Flag = 'on' | 'off' | 'unset';
type Pattern = 'on' | 'off' | 'unset';

export default class Validator {

  static specialCharacters = '!”§$%&\\(\\)=?\\*+~\'#.:,;^°\\/*\\{\\}\\[\\]\\|<>`´';
  static passwordConstraint = '^(?=.*[0-9])(?=.*[a-z])(?=.*[A-Z]).{8,}$';

  static specialEmailCharacters = '^\\s*[a-zA-Z0-9\.\_\+\\-]+@[a-zA-Z0-9\.\\-]+\.[a-zA-Z]{2,14}\\s*$';
  static numbers = '0-9';

  static requiredDefaultText = 'please-fill-in-this-field';

  private _requiredText = '';
  private _targets! : HTMLValidationElement[];

  private _noEmpty: Flag = 'unset';
  private _required: Flag = 'unset';

  private _noSpecialCharacters: Pattern = 'unset';
  private _passwordConstraint: Pattern = 'unset';
  private _noNumbersAndSpecialCharacters: Pattern = 'unset';
  private _noNumberAndEmail: Pattern = 'unset';

  private _minLength = 0;


  constructor( targets: HTMLInputElement[] ) {

    this._targets = targets;
    i18n.on('languageChanged', this._languageChanged );
    this._init( targets );
  }

  private _init( targets: HTMLValidationElement[] ) {
    targets.forEach( target => {
      if( target._validator ) return;
      target._validator = this;

      target.addEventListener( 'invalid', this._onValidity );
      target.addEventListener( 'input', this._onValidity );

      target.spellcheck = false;
      target.autocapitalize = 'off';
    })
  }

  /**
   *
   * @param targets
   */
  add( targets: HTMLInputElement[] ) {
    this._targets = [...this._targets, ...targets ];
    this._init( targets );

    if( this._required !== 'unset' ) this.required( this._required );
    if( this._required !== 'unset' ) this.noSpecialCharacters( this._noSpecialCharacters );
    if( this._required !== 'unset' ) this.passwordPolicy( this._passwordConstraint );
    if( this._required !== 'unset' ) this.isNumberOrEmail( this._noNumberAndEmail );
    if( this._required !== 'unset' ) this.minLength( this._minLength );
    if( this._required !== 'unset' ) this.noNumbersAndSpecialCharacters( this._noNumbersAndSpecialCharacters );
  }

  private _languageChanged = () => {

    this._targets.forEach( target => {

      const event = new CustomEvent( 'input' );
      target.dispatchEvent( event );

    } );
  }

  /**
   * Checking validity state interface and apply translations.
   * https://developer.mozilla.org/en-US/docs/Web/API/ValidityState
   * @param event
   */

  private _onValidity = ( event: Event ) => {

    const target = event.target as HTMLInputElement;
    if(this._noNumberAndEmail === 'on' && target.validity.patternMismatch) {
      target.setCustomValidity(i18n.t('no-number-and-email'));
    } else if ( this._passwordConstraint === 'on' && target.validity.patternMismatch ) {
      target.setCustomValidity( i18n.t( 'password-validation-error' ) );
    }else if ( target.type === 'email' && target.validity.patternMismatch ) {
      target.setCustomValidity( i18n.t( 'please-enter-a-valid-email-address' ) );
    } else if( target.pattern && target.validity.patternMismatch ) {
      target.setCustomValidity( i18n.t('invalid-characters') );
    } else if ( target.required && target.validity.valueMissing ) {
      target.setCustomValidity( i18n.t( this._requiredText ? this._requiredText : Validator.requiredDefaultText ) );
    }  else if ( this._noEmpty && !target.value.trim().length ) {
      target.setCustomValidity( i18n.t( 'this-field-cannot-be-left-blank' ) );
    } else {
      target.setCustomValidity( '' );
    }
  }

  required( value: Flag = 'on', text = '' ) {

    this._required = value;

    this._targets.forEach( target => {
      if( value === 'off') {
        target.required = false;
      } else if ( value === 'on' ) {
        target.required = true;
        if( text ) this._requiredText = text;
      }
    } )
    return this;
  }

  noEmpty( noEmpty: Flag = 'on' ) {

    this._noEmpty = noEmpty;

    return this;
  }

  noSpecialCharacters( value: Pattern = 'on' ) {

    this._noSpecialCharacters = value;

    this._targets.forEach( target => {
      if( value === 'off' ) {
        // target.pattern = ``;
        // Pattern attribute needs to be removed completely
        // or else pattern even if empty will still be validated
        target.removeAttribute( 'pattern' );
      } else if( value === 'on' && target.type === 'email' ) {
        target.pattern = Validator.specialEmailCharacters;
      } else if ( value === 'on' ) {
        target.pattern = `[^${ Validator.specialCharacters }]+`;
      }
    })
    return this;
  }

  passwordPolicy( value: Pattern = 'on' ) {

    this._passwordConstraint = value;

    this._targets.forEach( target => {
      if( value === 'off' ) {
        target.removeAttribute( 'pattern' );
      } else if( value === 'on' ) {
        target.pattern = Validator.passwordConstraint;
      }
    })
    return this;
  }

  noNumbersAndSpecialCharacters( value: Pattern = 'on', numbers: string = Validator.numbers, characters: string = Validator.specialCharacters  ) {

    this._noNumbersAndSpecialCharacters = value;

    this._targets.forEach( target => {
      if( target.type !== 'email' && value === 'on' ) {
        target.pattern = `[^${ numbers }+` + `^${ characters }]+`;
      } else if( value === 'off' ) {
        target.removeAttribute( 'pattern' );
      }
    })
    return this;
  }

  isNumberOrEmail(value: Pattern = 'on' ) {

    this._noNumberAndEmail = value;

    this._targets.forEach(target => {
      if (value === 'on') {
        target.pattern = `(^[0-9]*$)|${Validator.specialEmailCharacters}`;
      } else if ( value === 'off' ) {
        target.removeAttribute( 'pattern' );
      }
    })
    return this;
  }

  minLength( length = 2 ) {
    this._minLength = length;
    this._targets.forEach( target => {
      if( target.type !== 'email' ) target.minLength = length;
    })
    return this;
  }

  spellcheck() {
    this._targets.forEach( target => {
      if( target.spellcheck ) {
        target.spellcheck = false;
      } else {
        target.spellcheck = true;
      }

    } )
    return this;
  }

  autocapitalize() {
    this._targets.forEach( target => {
      if( target.autocapitalize === 'on' ) {
        target.autocapitalize = 'off';
      } else {
        target.autocapitalize = 'on';
      }
    } )

    return this;
  }



  destroy() {
    i18n.off( 'languageChanged', this._languageChanged );
    this._targets.forEach( target => {
      target.removeEventListener( 'invalid', this._onValidity );
      target.removeEventListener( 'input', this._onValidity );
      target._validator = null;
    } );
    return this;
  }

}
